import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, setDoc, doc, getDoc } from 'firebase/firestore'; // Added getDoc
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function PlanSettings() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState(null); // Added for error handling
  const auth = getAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userRef = doc(db, 'users', currentUser.uid);
        try {
          const userSnap = await getDoc(userRef);
          if (userSnap.exists() && userSnap.data().role === 'admin') {
            setIsAdmin(true);
            await fetchPlanSettings();
          } else {
            navigate('/plans');
          }
        } catch (err) {
          console.error('Error checking admin status:', err);
          setError('Failed to verify admin status: ' + err.message);
          navigate('/plans');
        }
      } else {
        navigate('/login');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  const fetchPlanSettings = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'planSettings'));
      const plansData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      if (plansData.length === 0) {
        const defaultPlans = [
          { id: 'basic', name: 'Basic', credits: 30, price: 0 },
          { id: 'pro', name: 'Pro', credits: 300, price: 600 },
          { id: 'enterprise', name: 'Enterprise', credits: 3000, price: 6000 },
        ];
        setPlans(defaultPlans);
        await Promise.all(
          defaultPlans.map((plan) => setDoc(doc(db, 'planSettings', plan.id), plan))
        );
      } else {
        setPlans(plansData);
      }
    } catch (error) {
      console.error('Error fetching plan settings:', error);
      toast.error('Failed to load plan settings.');
      setError('Failed to load plan settings: ' + error.message);
    }
  };

  const handleUpdatePlan = async (planId, updatedPlan) => {
    try {
      await setDoc(doc(db, 'planSettings', planId), updatedPlan, { merge: true });
      setPlans(plans.map((p) => (p.id === planId ? { ...p, ...updatedPlan } : p)));
      toast.success('Plan updated successfully!');
    } catch (error) {
      console.error('Error updating plan:', error);
      toast.error('Failed to update plan.');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-600">Loading...</div>;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 p-6 rounded-lg shadow-md text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 p-6 rounded-lg shadow-md text-center">
          <p className="text-red-600 mb-4">Access denied. Admins only.</p>
          <button
            onClick={() => navigate('/plans')}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
          >
            Back to Plans
          </button>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 font-['Noto Sans'] py-12 px-4 sm:px-6 lg:px-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="max-w-4xl mx-auto">
        <h2 className="text-4xl font-extrabold text-center mb-10 text-gray-700">Plan Settings</h2>
        <div className="space-y-6">
          {plans.map((plan) => (
            <div key={plan.id} className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <h3 className="text-2xl font-semibold text-gray-700 mb-4">{plan.name}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-600">Credits</label>
                  <input
                    type="number"
                    value={plan.credits}
                    onChange={(e) =>
                      setPlans(plans.map((p) =>
                        p.id === plan.id ? { ...p, credits: parseInt(e.target.value) || 0 } : p
                      ))
                    }
                    className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-600">Price ($/month)</label>
                  <input
                    type="number"
                    value={plan.price}
                    onChange={(e) =>
                      setPlans(plans.map((p) =>
                        p.id === plan.id ? { ...p, price: parseInt(e.target.value) || 0 } : p
                      ))
                    }
                    className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    disabled={plan.id === 'basic'} // Basic is free
                  />
                </div>
                <button
                  onClick={() => handleUpdatePlan(plan.id, { credits: plan.credits, price: plan.price })}
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors duration-300"
                >
                  Save Changes
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

export default PlanSettings;